/* eslint-disable */
import { TOKEN_KEY } from '@/common/constant';
const OBTAIN_TOKEN = '/oauth2/token';
import config from '@/config';
const { client_id, client_secret, ssoUrl } = config;

const axios = require('axios').default;
const helper = require('./helper');
const get = require('lodash/get');
const isError = require('lodash/isError');
const POS_CLIENT_CONFIG_HASH = 'CONFIG_HASH';
const POS_CLIENT_REQUESTS = 'POS_CLIENT_REQUESTS';
const SAVE_REQUEST_LIMIT = 100;
const COOKIE_NAME = 'pos-admin-frontend-deviceId';
const IS_REFRESHING_TOKEN = 'is_refreshing_token';
const LAST_REFRESHING_TOKEN_TIME = 'last_refreshing_token_time';

class ClientApi {
  constructor(url, apiKey, secret, deviceId, token, xBusinessGuid) {
    this.url = url;
    this.apiKey = apiKey;
    this.secret = secret;
    this.deviceId = deviceId ? deviceId : this._generateDeviceId();
    this._token = token;
    this._xBusinessGuid = xBusinessGuid;
    this._requests = [];
    this._pendingRequest = {};
    this._config = {};
    this._updatingConfig = false;

    this.axiosInstance = axios.create({
      baseURL: url,
      headers: {
        'x-api-key': apiKey,
        'x-api-secret': secret,
        'x-device-identifier': this.deviceId,
      },
    });

    // Generate the request id before sending request
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const { headers, method, url } = config;
        const randomChars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const after = helper.randomString(5, randomChars);
        const requestId = `${this.uniqid()}.Q${after}`;
        if (this._token) {
          headers.Authorization = `Bearer ${this._token}`;
        }
        if (this._xBusinessGuid) {
          headers['x-business-guid'] = this._xBusinessGuid;
        }
        headers['x-request-id'] = requestId;
        this._pendingRequest[requestId] = {
          requestId,
          startTime: new Date(),
          method,
          url,
        };
        return config;
      },
      (error) => Promise.reject(error),
    );

    this.axiosInstance.interceptors.response.use(
      (response) => {
        const request = this._buildRequestDataToSave(response);
        this._saveRequests(request);
        return response;
      },
      (error) => {
        const request = this._buildRequestDataToSave(error.response);
        this._saveRequests(request);
        return { error, success: false };
      },
    );
  }

  _buildRequestDataToSave(response) {
    const {
      config: { method },
      status,
      headers,
      data,
    } = response;
    const requestId = headers['x-request-id'] || data?.requestId || '';
    const configHash = headers['x-application-config-hash'];
    if (this._pendingRequest[requestId]) {
      const pendingRequest = this._pendingRequest[requestId];
      const duration = new Date() - pendingRequest.startTime;
      if (pendingRequest.url !== '/config/application') {
        this._checkConfig(configHash);
      }

      const request = {
        host: response.config.baseURL,
        method,
        url: pendingRequest.url,
        requestId,
        responseId: headers['x-response-id'] || data?.responseId || '',
        status,
        duration,
        time: pendingRequest.startTime,
        fullUrl: response.request.responseURL,
      };
      delete this._pendingRequest[requestId];
      return request;
    }
    return null;
  }

  _buildFailRequestDataToSave(response) {
    const {
      config: { headers, method },
    } = response;
    const requestId = headers['x-request-id'];
    const configHash = headers['x-application-config-hash'];
    const pendingRequest = this._pendingRequest[requestId];
    const duration = new Date() - pendingRequest.startTime;
    if (pendingRequest.url !== '/config/application') {
      this._checkConfig(configHash);
    }

    const request = {
      method,
      url: pendingRequest.url,
      requestId,
      responseId: null,
      duration,
      time: pendingRequest.startTime,
    };
    delete this._pendingRequest[requestId];
    return request;
  }

  refresh = async (resolve, reject, error) => {
    try {
      const lastRefreshTime = localStorage.getItem(LAST_REFRESHING_TOKEN_TIME);
      if (lastRefreshTime && new Date() - new Date(lastRefreshTime) < 10000) {
        return setTimeout(() => {
          localStorage.removeItem(IS_REFRESHING_TOKEN);
        }, 3000);
      }

      localStorage.setItem(IS_REFRESHING_TOKEN, true);
      localStorage.setItem(LAST_REFRESHING_TOKEN_TIME, new Date().toISOString());
      const token = JSON.parse(localStorage.getItem(TOKEN_KEY));
      const queryString = new URLSearchParams({
        client_id,
        client_secret,
        grant_type: 'refresh_token',
        refresh_token: token?.refresh_token,
      }).toString();
      const newToken = await this.request('post', OBTAIN_TOKEN, queryString, {
        baseURL: ssoUrl,
      });
      const { expires_in } = newToken;
      const currentDate = new Date();
      const expires_date = currentDate.setSeconds(currentDate.getSeconds() + expires_in - 10);
      localStorage.setItem(TOKEN_KEY, JSON.stringify({ ...newToken, expires_date }));
      this.setToken(newToken.access_token);
      localStorage.removeItem(IS_REFRESHING_TOKEN);
      if (resolve && error) {
        resolve(await this.axiosInstance.request(error.config));
      }
    } catch (err) {
      localStorage.removeItem(IS_REFRESHING_TOKEN);
      localStorage.removeItem(TOKEN_KEY);
      if (reject) {
        reject(err);
      } else {
        location.reload();
      }
    }
  };

  setToken(token) {
    this._token = token;
  }

  setXBusinessGuid(value) {
    this._xBusinessGuid = value;
  }

  async getConfig() {
    try {
      if (!this._updatingConfig) {
        this._updatingConfig = true;
        const res = await this.axiosInstance.get('/config/application');
        if (isError(res)) {
          throw res;
        }
        const { headers, data } = res;
        const appConfigHash = headers['x-application-config-hash'] || null;
        this._saveConfig(appConfigHash, data);
        this._updatingConfig = false;
        return data;
      }
    } catch (e) {
      return Promise.reject(e);
    }
  }

  _saveConfig(hash, data) {
    if (typeof localStorage !== 'undefined') {
      const saveData = JSON.stringify({
        [hash]: data,
      });
      localStorage.setItem(POS_CLIENT_CONFIG_HASH, saveData);
    } else {
      this._config = {
        [hash]: data,
      };
    }
  }

  _checkConfig(hash) {
    if (typeof localStorage !== 'undefined') {
      try {
        const configJSONStr = localStorage.getItem(POS_CLIENT_CONFIG_HASH) || null;
        const config = JSON.parse(configJSONStr);
        if (!config[hash]) {
          this.getConfig();
        }
        return config;
      } catch (_) {
        return null;
      }
    }
  }

  _saveRequests(request) {
    if (typeof localStorage !== 'undefined' && request) {
      try {
        let requests = this.getRequests();
        if (requests.length >= SAVE_REQUEST_LIMIT) {
          requests = requests.slice(1, SAVE_REQUEST_LIMIT);
        }
        requests.push(request);
        const requestsJSON = JSON.stringify(requests);
        localStorage.setItem(POS_CLIENT_REQUESTS, requestsJSON);
      } catch (e) {
        // reset requests value
        localStorage.setItem(POS_CLIENT_REQUESTS, '');
      }
    }
  }

  getRequests() {
    if (typeof localStorage !== 'undefined') {
      try {
        const requestsJSONStr = localStorage.getItem(POS_CLIENT_REQUESTS) || '[]';
        return JSON.parse(requestsJSONStr);
      } catch (e) {
        console.log(e);
      }
    }
  }

  async request(method, url, query, data) {
    return new Promise(async (resolve, reject) => {
      if (localStorage.getItem(IS_REFRESHING_TOKEN) && url !== OBTAIN_TOKEN) {
        setTimeout(async () => {
          resolve(await this.request(method, url, query, data));
        }, 1000);
      } else {
        try {
          const result = await this.axiosInstance[method](url, query, data);

          if (result.error) {
            const responseError = result.error.response;
            if (responseError.status === 401) {
              if (localStorage.getItem(IS_REFRESHING_TOKEN)) {
                resolve(await this.request(method, url, query, data));
              } else {
                await this.refresh();
              }
            } else {
              reject({
                responseError,
                error: result.error,
                success: false,
              });
            }
          } else {
            resolve(result.data);
          }
        } catch (err) {
          reject(err);
        }
      }
    });
  }

  getRequestsData() {
    return this._requests;
  }

  uniqid(prefix, more_entropy) {
    if (typeof prefix === 'undefined') {
      prefix = '';
    }

    let retId;
    const formatSeed = function (seed, reqWidth) {
      seed = parseInt(seed, 10).toString(16); // to hex str
      if (reqWidth < seed.length) {
        // so long we split
        return seed.slice(seed.length - reqWidth);
      }
      if (reqWidth > seed.length) {
        // so short we pad
        return Array(1 + (reqWidth - seed.length)).join('0') + seed;
      }
      return seed;
    };

    // BEGIN REDUNDANT
    if (!this.php_js) {
      this.php_js = {};
    }
    // END REDUNDANT
    if (!this.php_js.uniqidSeed) {
      // init seed with big random int
      this.php_js.uniqidSeed = Math.floor(Math.random() * 0x75bcd15);
    }
    this.php_js.uniqidSeed++;

    retId = prefix; // start with prefix, add current milliseconds hex string
    retId += formatSeed(parseInt(new Date().getTime() / 1000, 10), 8);
    retId += formatSeed(this.php_js.uniqidSeed, 5); // add seed hex string
    if (more_entropy) {
      // for more entropy we add a float lower to 10
      retId += (Math.random() * 10).toFixed(8).toString();
    }

    return retId;
  }

  config() {
    if (typeof localStorage !== 'undefined') {
      try {
        const configJSONStr = localStorage.getItem(POS_CLIENT_CONFIG_HASH) || [];
        const configHash = JSON.parse(configJSONStr);
      } catch (e) {
        console.log(e);
      }
    }
  }

  getConfigByKey(key) {
    const configStr = localStorage.getItem('CONFIG_HASH') || null;
    if (!configStr) {
      return '';
    }

    const config = JSON.parse(configStr);
    const configData = Object.values(config)[0];
    return get(configData, `data.${key}`, '');
  }

  _generateDeviceId() {
    const cookie = helper.getCookie(COOKIE_NAME);
    if (cookie) return cookie;

    const cookieValue = this.uniqid(`${COOKIE_NAME}-${helper.randomString(20)}`);
    helper.setCookie(COOKIE_NAME, cookieValue, 30);
    return cookieValue;
  }
}

export { ClientApi };

// export default ClientApi;
